<template>
    <div class="hide-while-loading">
        <div class="card">
            <div class="card-body">
                <!-- Email From + Save, Send and Discard -->
                <div class="form-group">
                    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                        <div class="flex items-center input-group">
                            <div class="input-group-prepend">
                                <span class="input-group-text input-group-prepend-fixed h-10 rounded-r-none">From:</span>
                            </div>
                            <select
                                id="inlineFormCustomSelect"
                                class="custom-select mr-sm-2 rounded-right h-10 rounded-l-none"
                                @change="fetchMailboxSignature()"
                                v-model="email.from">
                                <option v-for="(mailbox, index) in app.state.user.mailboxes" :key="mailbox.email + index">
                                    {{ mailbox.email }}
                                </option>
                            </select>
                        </div>
                        <div class="flex items-center space-x-2 mt-2 sm:mt-0">
                            <button
                                :disabled="discardEmailForm.busy || emailForm.busy"
                                class="btn btn-primary waves-effect waves-light whitespace-nowrap"
                                @click="discard"
                            >
                                <i class="mdi mdi-cancel"></i> Discard
                            </button>
                            <button
                                :disabled="discardEmailForm.busy || emailForm.busy"
                                class="btn btn-primary waves-effect waves-light whitespace-nowrap"
                                @click="save"
                            >
                                <i class="mdi mdi-content-save"></i> Save
                            </button>
                            <button
                                :disabled="discardEmailForm.busy || emailForm.busy"
                                class="btn btn-primary waves-effect waves-light whitespace-nowrap"
                                @click="send"
                            >
                                <i class="mdi mdi-send"></i> {{ emailForm.busy && email.sent_status === "queued" ? "Sending..." : "Send" }}
                            </button>
                        </div>
                    </div>
                </div>
                <!-- To Address Input -->
                <div class="form-group" v-if="!email.in_reply_to">
                    <email-input
                        :class="emailForm.errors.has('to') ? 'border rounded border-red-400' : ''"
                        element-id="to-email"
                        v-model="email.to"
                        input-prefix="To: "
                        :limit="1"
                        :typeahead="true"
                        :email-id="email.id"
                        :assigned-emails="assignedEmailsList"
                        suggestions-api="/contacts/suggestions/search"
                        @validation-error="addEmailError('to')"
                        @contact-added="resetErrors"
                    ></email-input>
                    <span class="text-red-600 text-xs break-normal mt-1" v-show="emailForm.errors.has('to')">
                        {{ emailForm.errors.get("to") }}
                    </span>
                </div>
                <!-- Reply To Address -->
                <div class="form-group" v-if="email.in_reply_to">
                    <span style="font-weight: 400;">To: {{ email.to }}</span>
                    <!-- CC, BCC field toggle -->
                    <div v-show="!showCc || !showBcc" class="pull-right">
                        <a class="link" v-show="!showCc" href="#" @click.prevent="toggleCc()">cc </a>
                        <a class="link" v-show="!showBcc" href="#" @click.prevent="toggleBcc()"> bcc</a>
                    </div>
                </div>
                <!-- CC Address Input -->
                <div class="form-group" v-show="showCc">
                    <email-input
                        :class="emailForm.errors.has('cc') ? 'border rounded border-red-400' : ''"
                        v-model="email.cc"
                        :limit="10"
                        :typeahead="true"
                        :email-id="email.id"
                        :assigned-emails="assignedEmailsList"
                        :to-value="getToEmail"
                        :visibility="showCc"
                        input-prefix="Cc: "
                        suggestions-api="/contacts/suggestions/search"
                        element-id="cc-email"
                        @validation-error="addEmailError('cc')"
                        @contact-added="resetErrors"
                    ></email-input>
                    <span class="text-red-600 text-xs break-normal mt-1" v-show="emailForm.errors.has('cc')">
                        {{ emailForm.errors.get("cc") }}
                    </span>
                </div>
                <!-- BCC Address Input -->
                <div class="form-group" v-show="showBcc">
                    <email-input
                        :class="emailForm.errors.has('bcc') ? 'border rounded border-red-400' : ''"
                        element-id="bcc-email"
                        v-model="email.bcc"
                        input-prefix="Bcc: "
                        :limit="10"
                        :typeahead="true"
                        :email-id="email.id"
                        :assigned-emails="assignedEmailsList"
                        suggestions-api="/contacts/suggestions/search"
                        @validation-error="addEmailError('bcc')"
                        @contact-added="resetErrors"
                    ></email-input>
                    <span class="text-red-600 text-xs break-normal mt-1" v-show="emailForm.errors.has('bcc')">
                        {{ emailForm.errors.get("bcc") }}
                    </span>
                </div>
                <!-- CC, BCC field toggle -->
                <div v-show="!showCc || !showBcc" class="pull-right" v-if="!email.in_reply_to">
                    <a class="link" v-show="!showCc" href="#" @click.prevent="toggleCc()">cc </a>
                    <a class="link" v-show="!showBcc" href="#" @click.prevent="toggleBcc()"> bcc</a>
                </div>
                <!-- Email Subject -->
                <div class="form-group">
                    <input
                        class="form-control"
                        placeholder="Subject:"
                        v-model="email.subject"
                        :class="{'is-invalid': emailForm.errors.has('subject')}"
                        @keyup="updateSubject"
                        @keydown="emailForm.errors.forget('subject')"/>
                    <span class="text-red-600 text-xs break-normal mt-1" v-show="emailForm.errors.has('subject')">
                        {{ emailForm.errors.get("subject") }}
                    </span>
                </div>
                <!-- Quill Body Editor -->
                <div class="form-group">
                    <div class="quill-editor">
                        <quill-editor
                            ref="editor"
                            v-model="email.body"
                            v-on:ready="onEditorReady($event)"
                            :options="editorOption"
                            @blur="onEditorBlur($event)"
                            @focus="onEditorFocus($event)">
                            <div id="toolbar" slot="toolbar" class="editor-toolbar">
                                <!-- Add a bold button -->
                                <button class="ql-bold">Bold</button>
                                <button class="ql-italic">Italic</button>
                                <button class="ql-underline">Underline</button>
                                <button class="ql-blockquote">Blockquote</button>
                                <button class="ql-header" value="1">H1</button>
                                <button class="ql-header" value="2">H2</button>
                                <button class="ql-list" value="ordered"></button>
                                <button class="ql-list" value="bullet"></button>
                                <select class="ql-color">
                                    <option selected="selected"></option>
                                    <option value="#e60000"></option>
                                    <option value="#ff9900"></option>
                                    <option value="#ffff00"></option>
                                    <option value="#008a00"></option>
                                    <option value="#0066cc"></option>
                                    <option value="#9933ff"></option>
                                    <option value="#ffffff"></option>
                                    <option value="#facccc"></option>
                                    <option value="#ffebcc"></option>
                                    <option value="#ffffcc"></option>
                                    <option value="#cce8cc"></option>
                                    <option value="#cce0f5"></option>
                                    <option value="#ebd6ff"></option>
                                    <option value="#bbbbbb"></option>
                                    <option value="#f06666"></option>
                                    <option value="#ffc266"></option>
                                    <option value="#ffff66"></option>
                                    <option value="#66b966"></option>
                                    <option value="#66a3e0"></option>
                                    <option value="#c285ff"></option>
                                    <option value="#888888"></option>
                                    <option value="#a10000"></option>
                                    <option value="#b26b00"></option>
                                    <option value="#b2b200"></option>
                                    <option value="#006100"></option>
                                    <option value="#0047b2"></option>
                                    <option value="#6b24b2"></option>
                                    <option value="#444444"></option>
                                    <option value="#5c0000"></option>
                                    <option value="#663d00"></option>
                                    <option value="#666600"></option>
                                    <option value="#003700"></option>
                                    <option value="#002966"></option>
                                    <option value="#3d1466"></option>
                                </select>
                                <select class="ql-background">
                                    <option value="#000000"></option>
                                    <option value="#e60000"></option>
                                    <option value="#ff9900"></option>
                                    <option value="#ffff00"></option>
                                    <option value="#008a00"></option>
                                    <option value="#0066cc"></option>
                                    <option value="#9933ff"></option>
                                    <option selected="selected"></option>
                                    <option value="#facccc"></option>
                                    <option value="#ffebcc"></option>
                                    <option value="#ffffcc"></option>
                                    <option value="#cce8cc"></option>
                                    <option value="#cce0f5"></option>
                                    <option value="#ebd6ff"></option>
                                    <option value="#bbbbbb"></option>
                                    <option value="#f06666"></option>
                                    <option value="#ffc266"></option>
                                    <option value="#ffff66"></option>
                                    <option value="#66b966"></option>
                                    <option value="#66a3e0"></option>
                                    <option value="#c285ff"></option>
                                    <option value="#888888"></option>
                                    <option value="#a10000"></option>
                                    <option value="#b26b00"></option>
                                    <option value="#b2b200"></option>
                                    <option value="#006100"></option>
                                    <option value="#0047b2"></option>
                                    <option value="#6b24b2"></option>
                                    <option value="#444444"></option>
                                    <option value="#5c0000"></option>
                                    <option value="#663d00"></option>
                                    <option value="#666600"></option>
                                    <option value="#003700"></option>
                                    <option value="#002966"></option>
                                    <option value="#3d1466"></option>
                                </select>

                                <!-- Custom Options -->
                                <div class="custom-ql-toolbar-options">
                                    <!-- Attachments -->
                                    <button
                                        v-if="multiUploadUrl"
                                        type="button"
                                        class="editor-attachment"
                                        id="multi-attachment"
                                        @click="toggleMultiAttachmentModal()"
                                        :disabled="attachmentLimitReached">
                                        <i class="mdi mdi-upload-multiple"></i>
                                    </button>
                                    <button
                                        type="button"
                                        class="editor-attachment"
                                        id="attachment"
                                        @click="selectAttachment()"
                                        :disabled="attachmentLimitReached">
                                        <i class="mdi mdi-paperclip"></i>
                                    </button>
                                    <!-- Saved Replies -->
                                    <el-select
                                        class="editor-saved-reply" id="saved-reply"
                                        tabindex="-1"
                                        placeholder="Search Saved Replies"
                                        @change="populateSavedReply()"
                                        v-model="selectedSavedReply"
                                        :loading="loadingSavedReplies"
                                        :filterable=true>

                                        <el-option
                                            class="checklist-set-dropdown"
                                            tabindex="-1"
                                            v-for="savedReply in savedRepliesList"
                                            :key="savedReply.id"
                                            :label="savedReply.name"
                                            :value="savedReply.id">
                                            <div style="max-width: 500px">
                                                <span class="font-medium" style="line-height: 12px !important">{{ savedReply.name }}</span>
                                                <div>
                                                    <p class="small text-muted" style="line-height: 1em !important;">
                                                        {{ savedReply.body }}
                                                    </p>
                                                </div>
                                            </div>
                                        </el-option>
                                    </el-select>
                                </div>
                            </div>
                        </quill-editor>
                        <!-- Mailbox Signature -->
                        <div class="mailbox-signature">
                            <span v-html="mailboxSignature"></span>
                        </div>
                        <span
                            class="invalid-input"
                            :class="{'d-block': emailForm.errors.has('body')}"
                            v-show="emailForm.errors.has('body')">
                            {{ emailForm.errors.get("body") }}
                        </span>
                    </div>
                </div>
                <!-- Attachments -->
                <div class="form-group m-b-3">
                    <el-upload
                        class="attachment-uploader"
                        :action="attachmentUploadUrl()"
                        :headers="attachmentRequestHeaders"
                        :file-list="attachments"
                        :on-success="handleAttachmentSuccess"
                        :before-remove="removeAttachment"
                        :on-error="handleAttachmentError"
                        :on-preview="viewAttachment"
                        :before-upload="beforeAttachmentUpload">
                        <div id="attachment-container" class="hidden"></div>
                    </el-upload>
                </div>

                <portal to="contentOverlayPortal">
                    <multi-upload-component v-if="multiUploadUrl"
                                            :show="showMultiUploadModal"
                                            :media-url="multiUploadUrl"
                                            :model-type="modelType"
                                            :full-width="true"
                                            submit-label="Attach"
                                            additional-body-classes="mx-12"
                                            @media-selected="attachSelectedMedia"
                                            @close-modal="toggleMultiAttachmentModal()">
                    </multi-upload-component>
                </portal>
            </div>
        </div>
    </div>
</template>

<script>
import LoadSavedReplies from "../../mixins/load-email-saved-replies";
import { mapGetters } from "vuex";
import tagGroupsMixin from "../../mixins/tag-groups-mixin";

export default {
    name: "CreateEmail",
    mixins: [LoadSavedReplies, tagGroupsMixin],
    props: [
        "newEmail", // Draft email instance
        "loading", // Boolean to indicate the message is loading or being create
        "context" // A object containing more context to be passed with saved replies request
    ],
    data () {
        return {
            email: this.newEmail,
            emailForm: new AppForm({}),
            discardEmailForm: new AppForm({}),
            editorOption: {
                modules: {
                    toolbar: "#toolbar"
                },
                clipboard: {
                    matchVisual: false
                },
                formats: [
                    "background",
                    "bold",
                    "color",
                    "font",
                    "code",
                    "italic",
                    "link",
                    "size",
                    "strike",
                    "script",
                    "underline",
                    "blockquote",
                    "header",
                    "indent",
                    "list",
                    "align",
                    "code-block",
                    "formula"
                ]
            },
            shouldShowCc: false,
            shouldShowBcc: false,
            attachmentUrl: null,
            attachments: this.newEmail.media ?? [],
            attachmentRequestHeaders: window.axios.defaults.headers.common,
            lastAutoSave: null,
            selectedContact: null, // Contact selected from the suggested contacts
            mailboxSignature: null,
            showMultiUploadModal: false,
            mediaAttachedFromExistingMedia: [],
            attachmentLimit: 10,
        };
    },
    computed: {
        ...mapGetters(["mutableEmailContext"]),
        showCc () {
            return this.shouldShowCc || !_.isEmpty(this.email.cc);
        },
        showBcc () {
            return this.shouldShowBcc || !_.isEmpty(this.email.bcc);
        },
        isReply () {
            return this.email.in_reply_to !== undefined;
        },
        assignedEmailsList () {
            // Final emails list array which has benn already choosen
            let list = [];
            let to_emails = [];
            let cc_emails = [];
            let bcc_emails = [];

            if (this.email.to !== null) {
                to_emails = _.map(this.email.to, "email");
                list.push(to_emails);
            }

            if (this.email.cc !== null) {
                cc_emails = _.map(this.email.cc, "email");
                list.push(cc_emails);
            }

            if (this.email.bcc !== null) {
                bcc_emails = _.map(this.email.bcc, "email");
                list.push(bcc_emails);
            }

            return list.reduce((acc, val) => acc.concat(val), []);

        },
        getToEmail () {
            if (!_.isEmpty(this.email.to)) {
                if (this.email.to[0] !== undefined) {
                    return this.email.to[0];
                }
            }
        },

        multiUploadUrl () {
            if (this.mutableEmailContext.fileSelectionUrl != null) {
                return this.mutableEmailContext.fileSelectionUrl;
            } else if (this.context && this.context.model) {
                return "/attachments/multi/" + this.context.model.type + "/" + this.context.model.id;
            } else {
                return null;
            }
        },
        attachmentLimitReached () {
            return this.attachments && this.attachments.length >= this.attachmentLimit;
        },
        modelType () {
            if (this.mutableEmailContext.fileSelectionUrl != null) {
                // url should be /attachments/multi/modelType/modelId
                return "App\\" + this.mutableEmailContext.fileSelectionUrl.split("/")[3];
            } else if (this.context && this.context.model) {
                return "App\\" + this.context.model.type;
            }
        }
    },
    watch: {
        newEmail (newVal) {
            this.email = newVal;
            this.attachments = newVal.media;
        },
        "email.to": function () {
            if (!_.isEmpty(this.email.to)) {
                if (this.email.to[0] !== undefined) {
                    let toEmail = this.email.to[0];
                    if (toEmail.always_cc_other && toEmail.email_other && toEmail.email_other.length) {
                        this.shouldShowCc = true;
                    }
                }
            }
        }
    },
    methods: {
        addEmailError (field) {
            this.emailForm.setErrors({[field]: ["The " + field + " email is invalid."]});
        },
        resetErrors () {
            this.emailForm.resetStatus();
        },
        attachmentUploadUrl () {
            return "/emails/" + this.email.id + "/attachments/upload";
        },
        updateSubject () {
            this.$emit("subject-changed", this.email.subject);
        },
        send () {
            // Validate all tags replaced
            if (this.email.body && this.getAllTags().some(tag => this.email.body.includes(tag))) {
                this.emailForm.setErrors({body: ["You have unpopulated tags! Look for the ## ## and resolve them!"]});
                toastr.error("Resolve template tags before sending your email!");
                return;
            }

            if (this.attachments && this.attachments.length > 0) {
                this.saveEmail("queued");
                return;
            }

            // Check if user meant to add an attachment to the email
            let attachmentDetectKeys = ["attached", "attach", "attachment", "aangeheg", "aanvoegsel", "aanheg", "heg"];
            let attachmentKeyUsed;

            for (let i = 0; i < attachmentDetectKeys.length; i++) {
                let key = attachmentDetectKeys[i];
                if (this.email.body && this.email.body.includes(key)) {
                    attachmentKeyUsed = key;
                    break;
                }
            }

            if (attachmentKeyUsed) {
                let self = this;
                swal({
                    text: "You wrote \"" + attachmentKeyUsed + "\" but there are no files attached. Send your message anyway?",
                    type: "info",
                    showCancelButton: true,
                    confirmButtonText: "Send"
                }).then((result) => {
                    if (result.value) {
                        self.saveEmail("queued");
                    }
                });
            } else {
                this.saveEmail("queued");
            }
        },
        save () {
            this.saveEmail("draft");
        },
        saveEmail (status, autoSave) {
            this.email.sent_status = status;
            this.emailForm = new AppForm(this.email);

            if (this.context.hasOwnProperty("email_type") && this.context.hasOwnProperty("service_provider_id")) {
                this.emailForm.email_type = this.context.email_type;
                this.emailForm.service_provider_id = this.context.service_provider_id;
            }

            if (this.mediaAttachedFromExistingMedia.length > 0) {
                this.emailForm.media_from_existing = this.mediaAttachedFromExistingMedia;
            }

            App.patch("/emails/" + this.email.id + "/update", this.emailForm)
            .then((response) => {

                if (this.email.sent_status === "queued") {
                    toastr.success("Sending email...");
                    this.$emit("email-queued", response);
                    if (this.context.hasOwnProperty("email_type") && this.context.email_type === "quote-request") {
                        Bus.$emit("quote-request-sent");
                        Bus.$emit("new-contacts-available", "service provider");
                    }
                } else {
                    toastr.success("Draft saved!");
                    if (autoSave) {
                        this.lastAutoSave = moment();
                        Bus.$emit("refresh-activity-list");
                    } else {
                        this.$emit("close");
                    }
                }

                Bus.$emit("email-updated", response);
                this.mediaAttachedFromExistingMedia = [];
            })
            .catch(errors => {
                if (this.email.sent_status === "queued") {
                    toastr.error("Sending failed! " + errors.message);
                } else if (this.email.sent_status === "draft") {
                    if (errors.message) {
                        toastr.error(errors.message);
                    } else {
                        toastr.error("Could not save draft. Invalid format!");
                    }
                } else {
                    toastr.error("Error saving email!");
                }

            });
        },
        discard () {
            let self = this;
            swal({
                title: "Discard email?",
                text: "Draft email will be deleted.",
                type: "warning",
                showCancelButton: true,
                confirmButtonText: "Delete"
            }).then((result) => {
                if (result.value) {
                    App.delete("/emails/" + self.email.id + "/delete", this.discardEmailForm)
                    .then(response => {
                        self.$emit("email-deleted");
                        toastr.info("Draft email deleted!");
                        Bus.$emit("email-deleted", self.email.id);
                        self.mediaAttachedFromExistingMedia = [];
                    })
                    .catch(errors => {
                        toastr.error(errors);
                    });
                }
            });
        },
        toggleCc () {
            this.shouldShowCc = !this.shouldShowCc;
        },
        toggleBcc () {
            this.shouldShowBcc = !this.shouldShowBcc;
        },
        selectAttachment () {
            $("#attachment-container").click();
        },
        populateSavedReply () {
            App.post("/savedreplies/" + this.selectedSavedReply + "/email/" + this.email.id + "/populate", new AppForm({
                context: this.context,
                contact: this.getEmailContact()
            })).then((response) => {

                //const value = JSON.parse(response.body);
                //const delta = this.$refs.editor.quill.clipboard.convert(value);

                this.email.body = JSON.parse(response.body);

                /*this.$refs.editor.quill.setContents(
                   delta
                );*/

                if (!_.isEmpty(response.subject)) {
                    this.email.subject = response.subject;
                }
                this.selectedSavedReply = null;
            }).catch(error => {
                this.selectedSavedReply = null;
                toastr.error("Error updating saved reply tags." + error.message);
            });
        },
        getEmailContact () {
            if (!_.isEmpty(this.selectedContact)) {
                return this.selectedContact.id;
            }

            if (!_.isEmpty(this.email.to)) {
                if (this.email.to[0].id !== undefined) {
                    return this.email.to[0].id;
                }
            }

            if (this.email.contact_id !== null) {
                return this.email.contact_id;
            }

            return null;
        },
        handleAttachmentSuccess (response, file, fileList) {
            if (!this.attachments) {
                this.attachments = [];
            }

            if (this.attachments.length < this.attachmentLimit) {
                this.attachments.push(response);
            } else {
                toastr.error("Only 5 Attachments Are Allowed per Email!");
                return null;
            }
        },
        handleAttachmentError (error, file, fileList) {
            if (error && error.message) {
                toastr.error(error.message);
                return;
            }

            toastr.error("Attachment upload failed! Please try again.");
        },
        beforeAttachmentUpload (file) {
            const isLt3M = file.size / 1024 / 1024 < 3;

            if (!isLt3M) {
                toastr.error("Email attachment size can not exceed 3MB!");
            }

            return isLt3M;
        },
        removeAttachment (file, fileList) {
            if (file === undefined || (file.id === undefined && file.response === undefined)) {

                if (file.is_uploaded) {
                    let index = this.attachments.indexOf(file);
                    index > -1 ? this.attachments.splice(index, 1) : null;

                    let existingMediaIndex = this.mediaAttachedFromExistingMedia.findIndex(media => media === file.uuid);
                    existingMediaIndex > -1 ? this.mediaAttachedFromExistingMedia.splice(index, 1) : null;
                }

                return true;
            }

            let mediaId = file.id !== undefined ? file.id : file.response.id;

            return new Promise((resolve, reject) => {

                axios.post("/emails/" + this.email.id + "/attachments/" + mediaId + "/delete", {_method: "DELETE"})
                .then(response => {
                    this.attachments.splice(this.attachments.indexOf(file), 1);
                    resolve(response.data);
                })
                .catch(errors => {
                    toastr.error(errors.response.data);
                    reject(errors.response);
                });

            });
        },
        viewAttachment (file) {
            window.open(file.url, "_blank");
        },
        onEditorBlur (editor) {
            if (this.email.sent_status === "new" || this.email.sent_status === "draft") {
                if (this.lastAutoSave === null || (moment().subtract(1, "minutes").isAfter(this.lastAutoSave))) {
                    this.saveEmail("draft", true);
                }
            }
        },
        onEditorFocus (editor) {
            this.emailForm.errors.forget("body");
        },
        onEditorReady (editor) {
            $("#toolbar").find(":button").attr("tabindex", "-1");
            $(".ql-picker-label").attr("tabindex", "-1");
            // editor.on("text-change", function (delta, oldDelta, source) { $(".ql-editor img").remove(); });
        },
        /**
         * Load the signature for the selected mailbox
         */
        fetchMailboxSignature () {
            axios.get("/emails/mailbox/signature", {
                params: {
                    "email": this.email.from
                }
            }).then(response => {
                this.mailboxSignature = response.data.email_signature;
            });
        },

        attachSelectedMedia (selectedAttachments) {
            if (!selectedAttachments) {
                toastr.error("Sorry, the selected media could not be attached! Please try again.");
                return;
            }

            selectedAttachments.forEach((attachment) => {
                if (this.attachments.length < this.attachmentLimit) {
                    this.attachments.push(attachment);
                    this.mediaAttachedFromExistingMedia.push(attachment.uuid);
                } else {
                    toastr.error(`Only ${this.attachmentLimit} Attachments Are Allowed per Email!`);
                    return null;
                }
            });
            this.toggleMultiAttachmentModal(false);
        },

        toggleMultiAttachmentModal (override = null) {
            this.showMultiUploadModal = override !== null ? override : !this.showMultiUploadModal;
        },

    },
    mounted () {
        this.fetchMailboxSignature();
        this.fetchSavedReplyOptions(this.email.id);
    },
};
</script>

<style>
.editor-saved-reply .el-input__inner {
    padding-left: 20px !important;
    font-size: 12px !important;
    font-weight: 400 !important;
    color: #565656 !important;
    height: 28px !important;
}

.editor-saved-reply .el-input__icon {
    line-height: 24px !important;
}

.ql-container {
    border-bottom: none !important;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
}

.mailbox-signature {
    margin-top: -3px;
    padding: 12px;
    border-top: none;
    border-left: 1px solid #dee2e6;
    border-right: 1px solid #dee2e6;
    border-bottom: 1px solid #dee2e6;
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
}

.mailbox-signature {
    line-height: 1.42;
    -moz-tab-size: 4;
    text-align: left;
    white-space: pre-wrap;
    word-wrap: break-word;
    font-family: sans-serif;
    font-size: 16px;
}

.mailbox-signature p {
    margin-bottom: 0;
}

.mailbox-signature img {
    max-width: 100%;
}
</style>
